// Searchable select (combobox). Drop-in replacement for components/Select `SelectField`.
// Props: label, options, selected, onSelect, onSearch, addNew, multiple, disabled, loading, error,
// placeholder, optional, optionIdKey, optionLabelKey, getOptionDisabled, noOptionsText,
// onListboxScroll, filterLimit, register (react-hook-form), custom (label sx), bgColor.
// Accepted and ignored (MUI-only): size, renderTags, multiline, recovery.
import { Popover } from "radix-ui";
import { Check, ChevronDown, Loader2, Plus, Search, X } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { Debounce } from "utils";
import { cn } from "./cn";
import { FieldError, Label } from "./Field";
import { sxToStyle } from "./sx";

export function SelectField({
  label,
  options = [],
  selected,
  onSelect,
  onSearch,
  addNew,
  multiple,
  disabled,
  loading,
  error,
  placeholder,
  optional,
  optionIdKey = "id",
  optionLabelKey = "name",
  getOptionDisabled,
  noOptionsText = "No options",
  onListboxScroll,
  filterLimit,
  register,
  custom,
  bgColor,
  className,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const listRef = useRef(null);
  const hiddenRef = useRef(null);
  const opts = Array.isArray(options) ? options : [];

  const labelOf = (o) => (typeof o === "string" ? o : String(o?.[optionLabelKey] ?? o?.name ?? ""));
  const keyOf = (o, i) => (typeof o === "string" ? o : (o?.[optionIdKey] ?? labelOf(o) ?? i));
  const values = multiple ? (Array.isArray(selected) ? selected : []) : selected ? [selected] : [];
  const isSelected = (o) => values.some((v) => labelOf(v) === labelOf(o));

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = q ? opts.filter((o) => labelOf(o).toLowerCase().includes(q)) : opts;
    if (filterLimit) list = list.slice(0, filterLimit);
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, query, filterLimit, optionLabelKey]);

  const canAdd = addNew && query.trim() !== "" && !opts.some((o) => labelOf(o) === query.trim());
  const items = canAdd ? [...filtered, { __add: true, inputValue: query.trim() }] : filtered;

  const report = (next) => {
    if (!register?.onChange || !register?.name) return;
    const value = multiple ? (next || []).map(labelOf).join(", ") : next ? labelOf(next) : "";
    // react-hook-form re-validates against the DOM value, so update it before React re-renders
    if (hiddenRef.current) hiddenRef.current.value = value;
    register.onChange({ target: { name: register.name, value }, type: "change" });
  };
  const select = (next) => {
    report(next);
    onSelect?.(next);
  };

  const choose = (o) => {
    if (o.__add) {
      addNew(o.inputValue);
      setQuery("");
      setOpen(false);
      return;
    }
    if (getOptionDisabled?.(o)) return;
    if (multiple) {
      select(isSelected(o) ? values.filter((v) => labelOf(v) !== labelOf(o)) : [...values, o]);
    } else {
      select(o);
      setOpen(false);
      setQuery("");
    }
  };

  const onQuery = (v) => {
    setQuery(v);
    setActive(0);
    if (onSearch) Debounce(() => onSearch(v));
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, items.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter" && items[active]) {
      e.preventDefault();
      choose(items[active]);
    }
  };

  const invalid = Boolean(error) && (multiple ? values.length === 0 : !selected);

  return (
    <div className={cn("relative mb-4", className)}>
      {label && (
        <Label optional={optional} error={invalid} style={sxToStyle(custom)}>
          {label}
        </Label>
      )}
      <Popover.Root open={open} onOpenChange={(o) => !disabled && setOpen(o)}>
        <Popover.Trigger asChild disabled={disabled}>
          <button
            type="button"
            style={bgColor ? { backgroundColor: bgColor } : undefined}
            className={cn(
              "flex min-h-10 w-full items-center gap-2 rounded-lg border border-line bg-panel px-3 py-1.5 text-left text-[13.5px] shadow-card outline-none transition-[border-color,box-shadow] hover:border-line-strong focus-visible:border-accent focus-visible:ring-3 focus-visible:ring-accent/20 disabled:cursor-not-allowed disabled:bg-[#f4f4f6]",
              open && "border-accent ring-3 ring-accent/20",
              invalid && "border-danger",
            )}
          >
            <span className="flex min-w-0 flex-1 flex-wrap items-center gap-1">
              {values.length === 0 && <span className="truncate text-faint">{placeholder || label || "Select…"}</span>}
              {!multiple && values[0] && <span className="truncate text-fg">{labelOf(values[0])}</span>}
              {multiple &&
                values.map((v, i) => (
                  <span key={keyOf(v, i)} className="inline-flex max-w-full items-center gap-1 rounded-md bg-accent-soft px-1.5 py-0.5 text-[12px] text-accent">
                    <span className="truncate">{labelOf(v)}</span>
                    <X
                      className="size-3 shrink-0 cursor-pointer"
                      onPointerDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        select(values.filter((x) => labelOf(x) !== labelOf(v)));
                      }}
                    />
                  </span>
                ))}
            </span>
            {!multiple && values[0] && !disabled && (
              <X
                className="size-4 shrink-0 text-faint hover:text-fg"
                aria-label="Clear"
                onPointerDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  select(null);
                }}
              />
            )}
            {loading ? <Loader2 className="size-4 shrink-0 animate-spin text-faint" /> : <ChevronDown className="size-4 shrink-0 text-faint" />}
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            align="start"
            sideOffset={4}
            className="z-[1400] w-[var(--radix-popover-trigger-width)] min-w-56 overflow-hidden rounded-xl border border-line bg-panel shadow-pop"
            onOpenAutoFocus={(e) => e.preventDefault()}
          >
            <div className="flex items-center gap-2 border-b border-line px-3">
              <Search className="size-4 text-faint" />
              <input
                autoFocus
                value={query}
                onChange={(e) => onQuery(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder="Search…"
                className="h-10 w-full border-0 bg-transparent p-0 text-[13.5px] text-fg shadow-none outline-none placeholder:text-faint focus:ring-0"
              />
            </div>
            <div ref={listRef} role="listbox" onScroll={onListboxScroll} className="max-h-64 overflow-y-auto p-1">
              {items.length === 0 && <div className="px-3 py-6 text-center text-[13px] text-muted">{loading ? "Loading…" : noOptionsText}</div>}
              {items.map((o, i) => {
                const dis = !o.__add && getOptionDisabled?.(o);
                const sel = !o.__add && isSelected(o);
                return (
                  <div
                    key={o.__add ? "__add" : `${keyOf(o, i)}-${i}`}
                    role="option"
                    aria-selected={sel}
                    aria-disabled={dis || undefined}
                    onMouseEnter={() => setActive(i)}
                    onClick={() => choose(o)}
                    className={cn(
                      "flex min-h-9 cursor-pointer items-center gap-2 rounded-md px-2.5 py-1.5 text-[13.5px] text-text",
                      i === active && "bg-black/[0.045]",
                      sel && "font-medium text-fg",
                      dis && "cursor-not-allowed opacity-45",
                    )}
                  >
                    {o.__add ? (
                      <>
                        <Plus className="size-4 text-accent" /> Add “{o.inputValue}”
                      </>
                    ) : (
                      <>
                        <span className="min-w-0 flex-1 truncate">{labelOf(o)}</span>
                        {sel && <Check className="size-4 text-accent" />}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
      {register?.name && (
        <input
          tabIndex={-1}
          aria-hidden
          readOnly
          name={register.name}
          ref={(el) => {
            hiddenRef.current = el;
            if (typeof register.ref === "function") register.ref(el);
          }}
          value={multiple ? values.map(labelOf).join(", ") : values[0] ? labelOf(values[0]) : ""}
          onFocus={() => !disabled && setOpen(true)}
          className="pointer-events-none absolute size-px opacity-0"
        />
      )}
      <FieldError>{typeof error === "string" ? error : null}</FieldError>
    </div>
  );
}

export default SelectField;
